import { motion } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import OpenAppBtn from '../../components/common/OpenAppBtn';
import { AmbientBackground } from '../../components/layout/AmbientBackground';
import { LoginGlassCard } from '../../components/login/LoginGlassCard';
import '../../components/login/login-page.css';
import GuestInquiryModal from '../../components/modals/GuestInquiryModal';
import SocialLoginButtons from '../../components/socialLogin/SocialLoginButtons';
import { ThirdPartyContext } from '../../contexts/ThirdPartyContext';
import SafariLoginInfoImage from '../../assets/login-info-image.svg';

export default function LoginMainPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { state: collaborationState, setState: collaborationSetState } = useContext(ThirdPartyContext);
  const [guestInquiryisOpen, setGuestInquiryIsOpen] = useState(false);
  const [showSafariPopup, setShowSafariPopup] = useState(false);

  useEffect(() => {
    if (!searchParams.has('provNo')) {
      return;
    }

    collaborationSetState({
      ...collaborationState,
      thirdPartyId: searchParams.get('provNo') || '',
      thirdPartyCode: searchParams.get('provNo') === '11' ? 'VITAPORT' : undefined,
      thirdPartyUserId: searchParams.get('mberNo') || '',
      orderNumber: searchParams.get('orderNo') || '',
    });
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const ua = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const uaLower = ua.toLowerCase();

    const isIOS = /(iphone|ipad|ipod)/i.test(uaLower) || /iP(hone|ad|od)/.test(navigator.platform || '');
    const isInApp = /(kakaotalk|naver|fbios|fbav|instagram|line|daumapps|whale)/i.test(ua);
    const isMobileSafari =
      /safari/i.test(ua) && /apple/i.test(vendor) && !/(CriOS|FxiOS|EdgiOS|OPiOS|OPR|EdgA)/.test(ua);
    const isMacOSSafari = /Macintosh/.test(ua) && /Version\/\d+.*Safari/.test(ua) && /Apple/.test(vendor);

    const shouldShow = ((isIOS && isMobileSafari) || isMacOSSafari) && !isInApp;

    if (shouldShow) setShowSafariPopup(true);
  }, []);

  return (
    <div className="soundpill-login-page bg-surface-container-lowest selection:bg-primary-container selection:text-on-primary-container">
      <AmbientBackground variant="login" />

      <div className="soundpill-login-page__open-app">
        <OpenAppBtn variant="login" />
      </div>

      {showSafariPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative w-[320px] rounded-[16px] bg-[#1C1C1E] px-6 pb-5 pt-9 text-center text-white">
            <img src={SafariLoginInfoImage} alt="Safari Login Info" className="mx-auto mb-4 w-[150px]" />
            <p className="mb-2 text-[15px] font-medium leading-[22px]">현재 아이폰 기본 브라우저(사파리)에서는</p>
            <p className="mb-3 text-[24px] font-bold">
              ⚠️ 카카오톡 로그인이
              <br />
              오류가 날 수 있어요!
            </p>
            <p className="mb-4 text-[19px] leading-[22px]">
              <span className="inline-block bg-[#1f3eef] p-[3px]">
                <span className="font-bold">이럴 땐 사운드필 앱</span>
              </span>
              으로
              <br />
              바로 접속해 주세요☺️
            </p>
            <button
              type="button"
              className="w-full rounded-[8px] bg-[#4F68FC] p-4 text-[16px] font-semibold text-white"
              onClick={() => setShowSafariPopup(false)}
            >
              확인했어요
            </button>
          </div>
        </div>
      )}

      <motion.div
        className="soundpill-login-page__center"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <main className="soundpill-login-page__center-inner">
          <LoginGlassCard>
            <div className="soundpill-login-page__card-inner">
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="soundpill-login-page__title">SoundPill</h1>
                <p className="soundpill-login-page__welcome">{t('authDraft.welcome')}</p>
              </div>

              <SocialLoginButtons variant="draft" />

              <div className="text-center">
                <p className="soundpill-login-page__hint">{t('authDraft.loginHint')}</p>
                <button
                  type="button"
                  className="soundpill-login-page__problem"
                  onClick={() => setGuestInquiryIsOpen(true)}
                >
                  {t('login.problem')}
                </button>
              </div>
            </div>
          </LoginGlassCard>
        </main>
      </motion.div>

      <GuestInquiryModal isOpen={guestInquiryisOpen} setIsOpen={setGuestInquiryIsOpen} />
    </div>
  );
}
